/**
 * Typing-signal throttle shared by both composers (dashboard agent + widget
 * visitor). Keystrokes call `input()`; a `typing isTyping:true` frame goes out
 * at most once per `activeMs`, and `isTyping:false` fires after `idleMs` of
 * silence (or immediately via `stop()`, e.g. when the message is sent).
 *
 * The caller supplies `send` — it builds the app-specific envelope; the server
 * re-derives actor identity from the socket, so clients only convey intent.
 */

export interface TypingSignaler {
  /** Call on every keystroke/draft change. */
  input: () => void;
  /** Flush an immediate stop (message sent, conversation switched, unmount). */
  stop: () => void;
}

export function createTypingSignaler(
  send: (isTyping: boolean) => void,
  activeMs = 2000,
  idleMs = 2500,
): TypingSignaler {
  let lastSent = 0;
  let active = false;
  let stopTimer: ReturnType<typeof setTimeout> | null = null;

  return {
    input() {
      const now = Date.now();
      if (!active || now - lastSent >= activeMs) {
        send(true);
        lastSent = now;
        active = true;
      }
      if (stopTimer) clearTimeout(stopTimer);
      stopTimer = setTimeout(() => {
        active = false;
        send(false);
      }, idleMs);
    },
    stop() {
      if (stopTimer) {
        clearTimeout(stopTimer);
        stopTimer = null;
      }
      if (active) {
        active = false;
        send(false);
      }
    },
  };
}
